import React, { lazy } from 'react'
import { Route, Switch } from 'react-router-dom'

import {
   ACCESS_DENIED_ROUTE_PATH,
   AGENT_SCHEDULES_ROUTE_PATH,
   APPLICATION_DETAILED_VIEW_ROUTE_PATH,
   CLOCK_IN_ROUTE_PATH,
   FEE_CALCULATOR_ROUTE_PATH,
   HOME_ROUTE_PATH,
   LOGIN_ROUTE_PATH,
   RECORD_DETAILED_VIEW_ROUTE_PATH
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import {
   showCRMWarningToastWithGivenMessage,
   showCRMSuccessToast
} from '@flowwai-frontend/bps-officer-web/common/ui'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import { useUsersnap } from '@flowwai-frontend/shared/common'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { Maps, MapsFeaturesEnum } from '@flowwai-frontend/shared/maps/ui'

import HomeRoute from '../../routes/HomeRoute/HomeRoute'
import LoginRoute from '../../routes/LoginRoute/LoginRoute'
import {
   FieldErrorMessageTooltip,
   FieldLabelTooltip
} from '@flowwai-frontend/shared-web/fields-v2/ui'

// region: common routes
const CallNotPermittedModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.CallNotPermittedModal
   }))
)

const CallNotProcessedModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.CallNotProcessedModal
   }))
)

const DirectCallModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.DirectCallModal
   }))
)

const UserAccessDeniedModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.UserAccessDeniedModal
   }))
)

const FileUploaderFieldSidePeek = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.FileUploaderFieldSidePeek
   }))
)

const FilePreviewCarouselModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.FilePreviewCarouselModal
   }))
)

const RecordPipelinesSelectionModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.RecordPipelinesSelectionModal
   }))
)

const SyncAssigneeFieldsUpdateHierarchyModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.SyncAssigneeFieldsUpdateHierarchyModal
   }))
)

export const ApplicationNotificationController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.ApplicationNotificationController
   }))
)

// region: boards routes
const BoardFilterModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.BoardFilterModal
   }))
)

const CreateBoardModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.CreateBoardModal
   }))
)

const CreateListModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.CreateListModal
   }))
)

const DeleteBoardListModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.DeleteBoardListModal
   }))
)

const DeleteBoardModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.DeleteBoardModal
   }))
)

const ListFilterModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.ListFilterModal
   }))
)

const RenameBoardModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.RenameBoardModal
   }))
)

const RenameListModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.RenameListModal
   }))
)

const ShareBoardListModel = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.ShareBoardListModel
   }))
)

const ShareBoardModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.ShareBoardModal
   }))
)
// endregion

// region: CRM item routes

const ApplicationDetailsV2Route = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/application-detailed-view').then(
      module => ({
         default: module.ApplicationDetailsV2Route
      })
   )
)

const RecordDetailedViewRoute = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/application-detailed-view').then(
      module => ({
         default: module.RecordDetailedViewRoute
      })
   )
)

const CompleteFieldResponseModal = lazy(() =>
   import('@flowwai-frontend/shared-web/fields/ui').then(module => ({
      default: module.CompleteFieldResponseModal
   }))
)

// endregion

// region: crm-item-creation routes
const LeadCreationModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/application-creation').then(
      module => ({
         default: module.LeadCreationModal
      })
   )
)
const CreateRecordModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/application-creation').then(
      module => ({
         default: module.CreateRecordModal
      })
   )
)
// endregion

// region: login routes
const AccessDeniedView = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/login').then(module => ({
      default: module.AccessDeniedView
   }))
)

const ProtectedRouteWrapper = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/login').then(module => ({
      default: module.ProtectedRouteWrapper
   }))
)
// endregion

// #region: iamroutes
const ClockInRoute = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/iam').then(module => ({
      default: module.ClockInRoute
   }))
)

const ClockInConfirmationModal = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/iam').then(module => ({
      default: module.ClockInConfirmationModal
   }))
)
// #endregion

// region: notifications
const PushNotificationController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/notifications').then(module => ({
      default: module.PushNotificationController
   }))
)
// endregion

// region: pipeline-item-scheduling routes
const AgentSchedulesRoute = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/scheduling').then(module => ({
      default: module.AgentSchedulesRoute
   }))
)
// endregion

const FeeCalculatorRoute = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/fee-calc').then(module => ({
      default: module.FeeCalculatorRoute
   }))
)

const LoginSuccessView = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/login').then(module => ({
      default: module.LoginSuccessView
   }))
)

//#region  Shared/Maps

//#endregion

export const Routes = () => {
   useUsersnap()

   const mapsT = useCustomT('crmItemDetailedView:geoLocationField')

   return (
      <>
         <PushNotificationController />
         <ApplicationNotificationController />
         <CreateBoardModal />
         <DeleteBoardModal />
         <CreateListModal />
         <DeleteBoardListModal />
         <BoardFilterModal />
         <ListFilterModal />
         <RenameListModal />
         <RenameBoardModal />
         <ShareBoardModal />
         <ShareBoardListModel />
         <CallNotPermittedModal />
         <CallNotProcessedModal />
         <DirectCallModal />
         <LeadCreationModal />
         <CreateRecordModal />
         <UserAccessDeniedModal />
         <FileUploaderFieldSidePeek />
         <FilePreviewCarouselModal />
         <CompleteFieldResponseModal />
         <ClockInConfirmationModal />
         <RecordPipelinesSelectionModal />
         <FieldErrorMessageTooltip />
         <FieldLabelTooltip />

         <SyncAssigneeFieldsUpdateHierarchyModal />

         <Maps
            serviceProvidersConfig={{
               maps: { serviceProvider: 'GOOGLE_MAPS' },
               search: { serviceProvider: 'GOOGLE_MAPS' }
            }}
            mapsT={mapsT}
            showCRMWarningToastWithGivenMessage={
               showCRMWarningToastWithGivenMessage
            }
            showCRMSuccessToast={showCRMSuccessToast}
            supportedFeatures={[
               MapsFeaturesEnum.OpenInGoogleMaps,
               MapsFeaturesEnum.CoordinatesList
            ]}
         />

         <Switch>
            <Route exact key={LOGIN_ROUTE_PATH} path={LOGIN_ROUTE_PATH}>
               <LoginRoute />
            </Route>
            <ProtectedRouteWrapper
               exact
               path={ACCESS_DENIED_ROUTE_PATH}
               key={ACCESS_DENIED_ROUTE_PATH}
            >
               <AccessDeniedView />
            </ProtectedRouteWrapper>
            <ProtectedRouteWrapper
               exact
               path={CLOCK_IN_ROUTE_PATH}
               key={CLOCK_IN_ROUTE_PATH}
            >
               <ClockInRoute />
            </ProtectedRouteWrapper>
            <ProtectedRouteWrapper
               path={APPLICATION_DETAILED_VIEW_ROUTE_PATH}
               key={APPLICATION_DETAILED_VIEW_ROUTE_PATH}
            >
               <ApplicationDetailsV2Route />
            </ProtectedRouteWrapper>
            <ProtectedRouteWrapper
               path={AGENT_SCHEDULES_ROUTE_PATH}
               key={AGENT_SCHEDULES_ROUTE_PATH}
            >
               <AgentSchedulesRoute />
            </ProtectedRouteWrapper>

            <ProtectedRouteWrapper
               path={RECORD_DETAILED_VIEW_ROUTE_PATH}
               key={RECORD_DETAILED_VIEW_ROUTE_PATH}
            >
               <RecordDetailedViewRoute />
            </ProtectedRouteWrapper>

            <ProtectedRouteWrapper
               path={FEE_CALCULATOR_ROUTE_PATH}
               key={FEE_CALCULATOR_ROUTE_PATH}
            >
               <FeeCalculatorRoute />
            </ProtectedRouteWrapper>

            <ProtectedRouteWrapper path={HOME_ROUTE_PATH} key={HOME_ROUTE_PATH}>
               {Config?.SHOW_LOGIN_SUCCESS_VIEW === 'true' ? (
                  <LoginSuccessView />
               ) : (
                  <HomeRoute />
               )}
            </ProtectedRouteWrapper>
         </Switch>
      </>
   )
}
